import { formatAmount } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { toast } from "@loot-os/ui/components/sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useCurrency } from "../lib/currency";
import { formatDate } from "../lib/format";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";
import { ErrorAlert } from "./ErrorAlert";
import { TableMessageRow } from "./TableMessageRow";

/** Store credit transfers not yet applied in Shopify, with a retry for one Shopify refused. */
export function ShopifyStoreCreditCard() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { can } = useTenantRole();
  const currency = useCurrency();
  const transfers = useQuery(trpc.shopify.transfers.queryOptions());
  const retryTransfer = useMutation(
    trpc.shopify.retryTransfer.mutationOptions({
      onSuccess: (data) => {
        void queryClient.invalidateQueries({ queryKey: trpc.shopify.pathKey() });
        if (data.status === "applied") toast.success("Store credit applied.");
        else toast.error(data.lastError ?? "Shopify refused the credit again.");
      },
    }),
  );

  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle>Store credit</CardTitle>
        <CardDescription>
          Store credit not yet applied in Shopify, oldest first. A transfer is retried every five
          minutes and waits for a manual retry after ten failures.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {transfers.error && <ErrorAlert message={transfers.error.message} />}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Transfer</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last error</TableHead>
              <TableHead>Created</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {(transfers.data ?? []).length === 0 && (
              <TableMessageRow colSpan={6}>Every transfer has been applied.</TableMessageRow>
            )}
            {(transfers.data ?? []).map((t) => (
              <TableRow key={t.id}>
                <TableCell>
                  {t.direction === "out" ? "Credit" : "Clawback"} · {t.reason.replaceAll("_", " ")}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatAmount(t.amountCents, currency)}
                </TableCell>
                <TableCell>
                  <Badge variant={t.status === "failed" ? "destructive" : "secondary"}>
                    {t.status}
                  </Badge>
                </TableCell>
                <TableCell className="max-w-64 truncate text-muted-foreground">
                  {t.lastError ?? "—"}
                </TableCell>
                <TableCell className="text-muted-foreground">{formatDate(t.createdAt)}</TableCell>
                <TableCell className="text-right">
                  {can("admin") && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={retryTransfer.isPending}
                      onClick={() => retryTransfer.mutate({ id: t.id })}
                    >
                      Retry
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
